/**
 * AdminDevelopmentCompletePage
 * UC-026 - Admin uploads app link and transitions project to ForTesting
 */
import React, { useState, useEffect } from 'react';
import { Button, Input, Card } from '../components/ui';
import { Upload, Link as LinkIcon, CheckCircle } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../core/api-client';
import toast from 'react-hot-toast';

interface Project {
  projectId: number;
  name: string;
  status: string;
  userId: number;
}

export function AdminDevelopmentCompletePage() {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const [project, setProject] = useState<Project | null>(null);
  const [appLink, setAppLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchProject = async () => {
    if (!projectId) return;

    try {
      setLoading(true);
      const response = await api.get<Project>(`/projects/${projectId}`);
      setProject(response);
    } catch (error) {
      console.error('Error fetching project:', error);
      toast.error(t('admin.development.complete.fetchError'));
      navigate('/admin/project-list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!projectId || !appLink.trim()) {
      toast.error(t('admin.development.complete.validation.appLinkRequired'));
      return;
    }

    // Validate URL format
    try {
      new URL(appLink);
    } catch {
      toast.error(t('admin.development.complete.validation.invalidUrl'));
      return;
    }

    try {
      setSubmitting(true);

      // Upload app link and transition to ForTesting
      await api.patch(`/projects/${projectId}`, {
        app_link: appLink
      });

      toast.success(t('admin.development.complete.success'));
      navigate('/admin/project-list');

    } catch (error) {
      console.error('Error uploading app link:', error);
      toast.error(t('admin.development.complete.uploadError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="text-center py-8">
          <div className="text-gray-600">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card className="p-6">
          <div className="text-center py-8">
            <div className="text-gray-600">{t('admin.development.complete.projectNotFound')}</div>
          </div>
        </Card>
      </div>
    );
  }

  // Check if project is in correct status
  if (project.status !== 'InDevelopment') {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card className="p-6">
          <div className="text-center py-8">
            <div className="text-amber-600 mb-4">
              <CheckCircle className="h-12 w-12 mx-auto" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('admin.development.complete.invalidStatus.title')}
            </h2>
            <p className="text-gray-600 mb-4">
              {t('admin.development.complete.invalidStatus.description', {
                status: project.status,
                required: 'InDevelopment'
              })}
            </p>
            <Button
              onClick={() => navigate('/admin/project-list')}
              variant="outline"
            >
              {t('common.back')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
          {t('admin.development.complete.title')}
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          {t('admin.development.complete.description')}
        </p>
      </div>

      <Card className="p-6">
        {/* Project Info */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            {t('admin.development.complete.projectInfo')}
          </h2>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="grid grid-cols-1 gap-2">
              <div>
                <span className="text-sm text-gray-600">{t('admin.development.complete.projectName')}:</span>
                <span className="ml-2 text-sm font-medium text-gray-900">{project.name}</span>
              </div>
              <div>
                <span className="text-sm text-gray-600">{t('admin.development.complete.projectId')}:</span>
                <span className="ml-2 text-sm font-medium text-gray-900">{project.projectId}</span>
              </div>
              <div>
                <span className="text-sm text-gray-600">{t('admin.development.complete.currentStatus')}:</span>
                <span className="ml-2 text-sm font-medium text-purple-600">{project.status}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Upload Form */}
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <LinkIcon className="h-4 w-4 inline mr-1" />
              {t('admin.development.complete.appLinkLabel')}
            </label>
            <Input
              type="url"
              value={appLink}
              onChange={(e) => setAppLink(e.target.value)}
              placeholder={t('admin.development.complete.appLinkPlaceholder')}
              required
              className="w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('admin.development.complete.appLinkHelp')}
            </p>
          </div>

          <div className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/project-list')}
              disabled={submitting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={submitting || !appLink.trim()}
              className="bg-green-600 hover:bg-green-700"
            >
              {submitting ? (
                <>
                  <Upload className="h-4 w-4 mr-2 animate-spin" />
                  {t('admin.development.complete.uploading')}
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {t('admin.development.complete.submit')}
                </>
              )}
            </Button>
          </div>
        </form>

        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h3 className="text-sm font-medium text-blue-800 mb-2">
            {t('admin.development.complete.nextSteps.title')}
          </h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• {t('admin.development.complete.nextSteps.statusChange')}</li>
            <li>• {t('admin.development.complete.nextSteps.notification')}</li>
            <li>• {t('admin.development.complete.nextSteps.testingPeriod')}</li>
          </ul>
        </div>
      </Card>
    </div>
  );
}

export default AdminDevelopmentCompletePage;
